import { Switch } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import GaiaDeleteButton from "~/modules/app/components/GaiaDeleteButton";
import GaiaDataGrid from "~/modules/app/components/GaiaTable";
import { renderItalicCell } from "~/modules/settings/util/dataGridHelper";

import { ResearchTypeSpeciesListCardFragment } from "./fragments.generated";

interface Props {
    researchTypeSpecies: ResearchTypeSpeciesListCardFragment[];
    isAdmin: boolean;
    onDeleteClick: (speciesId: string) => Promise<void>;
    onToggleShowToClientUserClick: (speciesId: string, value: boolean) => Promise<void>;
}

export default function ResearchTypeSpeciesDataGrid({
    researchTypeSpecies,
    isAdmin,
    onDeleteClick,
    onToggleShowToClientUserClick,
}: Props) {
    const { t } = useTranslation("researchType");

    const columns = useMemo(() => {
        const retVal: Array<GridColDef<ResearchTypeSpeciesListCardFragment>> = [
            {
                field: "name",
                headerName: t("researchTypeSpeciesDataGrid.headerName"),
                flex: 1,
                valueGetter: (_, record) => record.species.name,
            },
            {
                field: "scientificName",
                headerName: t("researchTypeSpeciesDataGrid.headerScientificName"),
                flex: 1,
                valueGetter: (_, record) => record.species.scientificName,
                renderCell: renderItalicCell,
            },
        ];

        if (isAdmin) {
            retVal.push({
                field: "showToClientUser",
                headerName: t("researchTypeSpeciesDataGrid.headerShowToClientUser"),
                flex: 1 / 2,
                valueGetter: (_, record) => record.showToClientUser,
                renderCell: params => (
                    <Switch
                        checked={params.value}
                        onChange={() => onToggleShowToClientUserClick(params.row.species.id, !params.value)}
                    />
                ),
            });

            retVal.push({
                type: "actions",
                field: "delete",
                flex: 1 / 2,
                headerName: t("researchTypeSpeciesDataGrid.headerDelete"),
                renderCell: params => (
                    <GaiaDeleteButton isLoading={false} onDelete={() => onDeleteClick(params.row.species.id)} />
                ),
            });
        }

        return retVal;
    }, [isAdmin, onDeleteClick, onToggleShowToClientUserClick, t]);

    return <GaiaDataGrid rows={researchTypeSpecies} columns={columns} rowCount={researchTypeSpecies.length} />;
}
